import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import imageCompression from "browser-image-compression";
import Layout from "../Layout";
import Loader from "../animations/Loader";
import DobInput from "../nsp/DobInput";
import { handleUK } from "../nsp/nspHandlers";
import StepRightToWork from "../nsp/steps/StepRightToWork";
import StepPolicies from "../nsp/steps/StepPolicies";
import StepSignAndSubmit from "../nsp/steps/StepSignAndSubmit";
import TrialTerms from "./TrialTerms";
import "../nsp/styles/newstart.scss";

const STEPS = ["Your details", "Right to work", "ID and address", "Next of kin", "Policies", "Trial letter"];

async function readIdFile(file) {
  if (!file) return "";
  const ext = file.name.split(".").pop().toLowerCase();
  if (ext !== "jpg" && ext !== "jpeg" && ext !== "png") return false;
  const compressed = await imageCompression(file, { maxSizeMB: 1, maxWidthOrHeight: 600 });
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(compressed);
  });
}

export default function TrialWizard() {
  const navigate = useNavigate();
  const url = process.env.REACT_APP_HR_API_URL;
  const signatureRef = useRef(null);
  const [step, setStep] = useState(0);
  const [linkStatus, setLinkStatus] = useState("validating");
  const [linkError, setLinkError] = useState("");
  const [trialId, setTrialId] = useState("");
  const [context, setContext] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dob, setDob] = useState("");
  const [EU, setEU] = useState(null);
  const [shareCode, setShareCode] = useState("");
  const [shareExpire, setShareExpire] = useState("");
  const [visaType, setVisaType] = useState("");
  const [visaReason, setVisaReason] = useState(null);
  const [showBox, setShowBox] = useState(false);
  const [poi, setPoi] = useState("");
  const [poa, setPoa] = useState("");
  const [nokFirst, setNokFirst] = useState("");
  const [nokLast, setNokLast] = useState("");
  const [nokPhone, setNokPhone] = useState("");
  const [ip, setIp] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get("id");
    const token = params.get("token");
    if (token) sessionStorage.setItem("onboardingToken", token);
    if (!id || (!token && !sessionStorage.getItem("onboardingToken"))) {
      setLinkStatus("invalid");
      setLinkError("Invalid or missing link. Please use the link from your email or reach out to our HR Team.");
      return;
    }
    setTrialId(id);
    const headers = { "x-onboarding-token": sessionStorage.getItem("onboardingToken") };
    axios
      .get(`${url}/trial/${id}`, { headers })
      .then((res) => {
        if (res.data.closed) {
          setLinkError("This trial shift link is no longer active. Please contact HR.");
          setLinkStatus("invalid");
        } else if (res.data.completed) {
          setDone(true);
          setLinkStatus("valid");
        } else {
          setContext(res.data);
          setFirstName(res.data.firstName || "");
          setMiddleName(res.data.middleName || "");
          setLastName(res.data.lastName || "");
          setLinkStatus("valid");
        }
      })
      .catch((err) => {
        setLinkStatus("invalid");
        setLinkError(err.response?.status === 401
          ? "Invalid or expired link. Please use the link from your email or reach out to our HR Team."
          : "Can not find any details for this individual.");
      });
    axios.get("https://api.ipify.org/?format=json").then((res) => setIp(res.data.ip || "")).catch(() => {});
  }, [url, navigate]);

  const showError = (text) => {
    setMessage(text);
    setTimeout(() => setMessage(""), 5000);
  };

  const updateRtw = (field, value) => {
    if (field === "shareCode") setShareCode(value);
    else if (field === "shareExpire") setShareExpire(value);
    else if (field === "visaType") setVisaType(value);
    else if (field === "visaReason") setVisaReason(value);
  };

  const handleUKChange = (uk) => {
    handleUK(uk, setEU, setShowBox);
    if (uk === "Yes") {
      setShareCode("");
      setShareExpire("");
      setVisaType("");
      setVisaReason(null);
    }
  };

  const validate = () => {
    if (step === 0) {
      if (!firstName.trim() || !lastName.trim() || !dob) {
        showError("First name, last name, and date of birth are required.");
        return false;
      }
      if (moment().diff(moment(dob, "YYYY-MM-DD"), "years") < 16) {
        showError("You must be at least 16 years old.");
        return false;
      }
    }
    if (step === 1) {
      if (EU === null) {
        showError("Please select whether you are a UK citizen.");
        return false;
      }
      if (EU === false) {
        const visaMissing = [];
        if (!shareCode?.trim()) visaMissing.push("Share code");
        if (!shareExpire) visaMissing.push("Visa expiry date");
        if (!visaType?.trim()) visaMissing.push("Visa type");
        if (visaType === "Other" && (!visaReason || visaReason == null)) {
          visaMissing.push("Details for visa type ‘Other’");
        }
        if (visaMissing.length > 0) {
          showError("Please complete your visa details: " + visaMissing.join(", "));
          return false;
        }
      }
    }
    if (step === 2 && !poi) {
      showError("Please upload a photo of your ID (jpg or png).");
      return false;
    }
    if (step === 2 && !poa) {
      showError("Please upload a proof of address (jpg or png).");
      return false;
    }
    if (step === 3 && (!nokFirst.trim() || !nokLast.trim() || !nokPhone.trim())) {
      showError("Next of kin first name, last name, and phone number are required.");
      return false;
    }
    if (step === 5 && (!signatureRef.current || signatureRef.current.isEmpty())) {
      showError("Please sign the trial shift letter.");
      return false;
    }
    return true;
  };

  const submit = async () => {
    if (!validate()) return;
    setBusy(true);
    const signature = signatureRef.current.getTrimmedCanvas().toDataURL("image/png");
    try {
      await axios.post(
        `${url}/trial/${trialId}/submit`,
        {
          firstName: firstName.trim(),
          middleName: middleName.trim(),
          lastName: lastName.trim(),
          dob,
          ukCitizen: EU,
          shareCode: EU === false ? shareCode.trim() : "",
          shareExpire: EU === false ? shareExpire : "",
          visaType: EU === false ? visaType.trim() : "",
          visaReason: EU === false && visaType === "Other" ? (visaReason || "").trim() : null,
          poi,
          poa,
          nokFirstName: nokFirst.trim(),
          nokLastName: nokLast.trim(),
          nokPhone: nokPhone.trim(),
          signature,
          ipAddress: ip,
        },
        { headers: { "x-onboarding-token": sessionStorage.getItem("onboardingToken") } }
      );
      setDone(true);
    } catch (err) {
      showError(err.response?.data?.error || err.response?.data || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (linkStatus === "validating") {
    return (
      <Layout>
        <Container maxWidth="sm" sx={{ py: { xs: 4, sm: 8 }, textAlign: "center" }}>
          <Loader />
        </Container>
      </Layout>
    );
  }
  if (linkStatus === "invalid") {
    return (
      <Layout>
        <Container maxWidth="sm" sx={{ py: { xs: 3, sm: 5 } }}>
          <Alert severity="error">{linkError}</Alert>
        </Container>
      </Layout>
    );
  }
  if (done) {
    return (
      <Layout>
        <Container maxWidth="sm" sx={{ py: { xs: 3, sm: 5 } }}>
          <Paper sx={{ p: { xs: 2, sm: 4 }, textAlign: "center" }}>
            <Typography variant="h5" sx={{ mb: 1 }}>Trial shift form received</Typography>
            <Typography variant="body1" color="text.secondary">
              Thank you. HR have your details and will be in touch about the trial shift.
            </Typography>
          </Paper>
        </Container>
      </Layout>
    );
  }

  const fullName = `${firstName} ${lastName}`.trim();

  return (
    <Layout>
      <Container maxWidth="sm" sx={{ py: { xs: 3, sm: 5 } }}>
        <Paper sx={{ p: { xs: 2, sm: 4 } }}>
          <Typography variant="h5" sx={{ mb: 0.5 }}>Trial shift</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {context?.companyName} · {context?.roleName}
            {context?.trialDate ? ` · ${moment(context.trialDate).format("DD/MM/YYYY HH:mm")}` : ""}
          </Typography>

          <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Step {step + 1} of {STEPS.length}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {STEPS[step]}
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={((step + 1) / STEPS.length) * 100}
            sx={{ mb: 3, borderRadius: 1, height: 6 }}
          />

          {message ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {typeof message === "string" ? message : "Something went wrong."}
            </Alert>
          ) : null}

          {step === 0 && (
            <Stack spacing={2}>
              <TextField
                required
                label="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
              <TextField
                label="Middle name"
                value={middleName}
                onChange={(e) => setMiddleName(e.target.value)}
              />
              <TextField
                required
                label="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Date of birth <Box component="span" color="error.main">*</Box>
                </Typography>
                <DobInput value={dob} onChange={setDob} />
              </Box>
            </Stack>
          )}

          {step === 1 && (
            <StepRightToWork
              data={{ EU, shareCode, shareExpire, visaType, visaReason }}
              update={updateRtw}
              showBox={showBox}
              onUKChange={handleUKChange}
            />
          )}

          {step === 2 && (
            <Stack spacing={2} alignItems="flex-start">
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                Proof of ID <Box component="span" color="error.main">*</Box>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Upload a photo of your passport or driving licence. JPG or PNG.
              </Typography>
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                {poi ? "Change ID photo" : "Upload ID photo"}
                <input
                  type="file"
                  hidden
                  accept="image/jpeg,image/png"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    const result = await readIdFile(file);
                    if (result === false) showError("Proof of ID must be a jpg or png image.");
                    else setPoi(result || "");
                  }}
                />
              </Button>
              {poi ? <Alert severity="success">ID image attached.</Alert> : null}
              <Typography variant="body1" sx={{ fontWeight: 600, pt: 1 }}>
                Proof of address <Box component="span" color="error.main">*</Box>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Upload a photo of a recent bill or bank statement that shows your name and address. JPG or PNG.
              </Typography>
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                {poa ? "Change address photo" : "Upload address photo"}
                <input
                  type="file"
                  hidden
                  accept="image/jpeg,image/png"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    const result = await readIdFile(file);
                    if (result === false) showError("Proof of address must be a jpg or png image.");
                    else setPoa(result || "");
                  }}
                />
              </Button>
              {poa ? <Alert severity="success">Address image attached.</Alert> : null}
            </Stack>
          )}

          {step === 3 && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  label="Next of kin first name"
                  value={nokFirst}
                  onChange={(e) => setNokFirst(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  label="Next of kin last name"
                  value={nokLast}
                  onChange={(e) => setNokLast(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  label="Next of kin phone"
                  value={nokPhone}
                  onChange={(e) => setNokPhone(e.target.value)}
                  inputProps={{ inputMode: "tel" }}
                />
              </Grid>
            </Grid>
          )}

          {step === 4 && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Please read the policies below. You do not need to sign them for a trial shift. If you are offered a job, you will be asked to agree to them on the new start paperwork.
              </Typography>
              <StepPolicies data={{ alcohol: false, enviromental: false, safety: false, deductions: false }} readOnly />
            </>
          )}

          {step === 5 && (
            <>
              <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
                <TrialTerms
                  companyName={context?.companyName}
                  siteName={context?.siteName}
                  address={context?.address}
                  roleName={context?.roleName}
                  trialDate={context?.trialDate}
                  trialEnd={context?.trialEnd}
                  reportingTo={context?.reportingTo}
                  fullName={fullName}
                />
              </Paper>
              <StepSignAndSubmit signatureRef={signatureRef} />
            </>
          )}

          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            {step > 0 && (
              <Button variant="outlined" onClick={() => setStep(step - 1)} disabled={busy}>Back</Button>
            )}
            {step < STEPS.length - 1 && (
              <Button variant="contained" onClick={() => validate() && setStep(step + 1)}>Next</Button>
            )}
            {step === STEPS.length - 1 && (
              <Button variant="contained" color="success" onClick={submit} disabled={busy}>
                {busy ? "Sending..." : "Submit"}
              </Button>
            )}
          </Stack>
        </Paper>
      </Container>
    </Layout>
  );
}
